import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const Gallery = ({ section = 'hotel', language = 'de' }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const containerRef = useRef(null);

  const translations = {
    de: { title: 'Erleben Sie mehr' },
    en: { title: 'GALLERY' }
  };

  const hotelImages = [
    '/hotelSlika6.png',
    '/hotelSlika1.png',
    '/hotelSlikaDodatna.png',
    '/hotelSlika3.png',
    '/galerijaHotel2.jpg',
    '/galerijaHotel6.jpg',
    '/galerijaHotel1.jpg',
    '/galerijaHotel4.jpg',
    '/galerijaHotel5.jpg',
    '/hotelNoveSlike/hotelN1.png',
    '/hotelNoveSlike/hotelN2.png',
    '/hotelNoveSlike/hotelN3.png',
    '/hotelNoveSlike/hotelN4.png',
    '/hotelNoveSlike/hotelN5.jpeg',
    '/hotelNoveSlike/hotelN6.jpeg',
    '/hotelNoveSlike/hotelN7.jpeg',
  ];

  const restaurantImages = [
    '/galerijaRestoran1.jpeg',
    '/galerijaRestoran2.jpeg',
    '/hotelSlika2.png',
    '/restoranSlikaDodatna.png',
    '/galerijaRestoran3.jpeg',
    '/galerijaRestoran4.jpeg',
    '/galerijaRestoran5.jpeg',
    '/galerijaRestoran6.jpeg',
    '/galerijaRestoran7.jpeg',
    '/restoran1.jpeg',
    '/restoran2.jpeg',
    '/restoran3.jpeg',
    '/restoran4.jpeg',
    // Slike hrane - red 2
    '/hranaSlike/hrana1.jpeg',
    '/hranaSlike/hrana2.jpeg',
    '/hranaSlike/hrana3.jpeg',
    '/hranaSlike/hrana4.jpeg',
    '/hranaSlike/hrana5.jpeg',
    '/hranaSlike/hrana6.jpeg',
    '/hranaSlike/hrana7.jpeg',
    '/hranaSlike/hrana8.jpeg',
    '/hranaSlike/hrana9.jpeg',
    '/hranaSlike/hrana10.jpeg',
    '/hranaSlike/hrana11.jpeg',
    '/hranaSlike/hrana12.jpeg',
    '/hranaSlike/hrana13.jpeg',
    '/hranaSlike/hrana14.jpeg',
    '/hranaSlike/hrana15.jpeg',
    '/hranaSlike/hrana17.jpeg',
    '/hranaSlike/hrana18.jpeg',
    '/hranaSlike/hrana19.jpeg',
    '/hranaSlike/hrana20.jpeg',
    '/hranaSlike/hrana21.jpeg',
    '/hranaSlike/hrana22.jpeg',
  ];

  const images = section === 'hotel' ? hotelImages : restaurantImages;

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(images[index]);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % images.length;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + images.length) % images.length;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (!selectedImage) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedImage, currentIndex]);

  return (
    <section 
      id="gallery" 
      className={`relative py-32 overflow-hidden ${
        section === 'restaurant' ? 'bg-gradient-to-b from-luxury-charcoal via-black/80 to-luxury-charcoal' : 'bg-luxury-charcoal'
      }`}
    >
      <div className="container-custom relative z-10">
        
        {/* LUXURY BRUTAL Header */}
        <div className="mb-16 md:mb-24 relative flex flex-col items-center justify-center">
          {/* Main Title */}
          <h2 className="text-[4rem] md:text-[6rem] lg:text-[8rem] font-black leading-none tracking-tighter text-center relative text-white mx-auto">
            {translations[language].title}
          </h2>
          
          {/* Gold accent bars */}
          <div className="mt-4 flex gap-2 justify-center">
            <div className="w-32 h-1 bg-luxury-gold" />
            <div className="w-16 h-1 bg-luxury-gold/50" />
            <div className="w-8 h-1 bg-luxury-gold/30" />
          </div>
        </div>

        {/* DYNAMIC GRID LAYOUT - Automatically fits all images */}
        <div ref={containerRef} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {images.map((image, index) => (
            <div
              key={index}
              className="group relative overflow-hidden cursor-pointer aspect-[4/3] rounded-lg"
              onClick={() => openLightbox(index)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="relative w-full h-full">
                <img
                  src={image}
                  alt={`Gallery image ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 md:duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-500 md:duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 md:duration-300">
                  <div className="relative">
                    <div className="w-10 h-0.5 bg-white" />
                    <div className="w-0.5 h-10 bg-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SIMPLE LIGHTBOX */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[999] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 md:top-8 md:right-8 w-10 h-10 md:w-12 md:h-12 bg-white/10 hover:bg-white/20 text-white transition-all flex items-center justify-center z-50 rounded-full"
          >
            <X size={24} strokeWidth={2} />
          </button>

          {/* Previous arrow */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white/10 hover:bg-white/20 text-white transition-all z-50 flex items-center justify-center rounded-full"
          >
            <div className="w-0 h-0 border-t-[6px] border-t-transparent border-r-[10px] border-r-white border-b-[6px] border-b-transparent" />
          </button>

          {/* Next arrow */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white/10 hover:bg-white/20 text-white transition-all z-50 flex items-center justify-center rounded-full"
          >
            <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-white border-b-[6px] border-b-transparent" />
          </button>

          {/* Image */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-[90vw] h-[80vh] md:w-[85vw] md:h-[85vh] flex items-center justify-center"
          >
            <img
              src={selectedImage}
              alt=""
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
